import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores';
import { API_URL, formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Calendar,
  Camera,
  Loader2,
  Trash2,
  Link2,
  Receipt,
  FileImage,
  UploadCloud,
  X // <-- NOUVEAU: Import ajouté pour le bouton d'effacement
} from 'lucide-react';

const EXPENSE_CATEGORIES = [
  { value: 'material', label: 'Matériel' },
  { value: 'transport', label: 'Transport' },
  { value: 'food', label: 'Restauration' },
  { value: 'service', label: 'Service' },
  { value: 'other', label: 'Autre' },
];

const ExpenseCard = ({ expense, events, onDelete, onLinkEvent }) => {
  const eventName = events?.find((e) => e.id === expense.event_id)?.title;

  return (
    <Card className="card-hover" data-testid={`expense-card-${expense.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium">{expense.description || 'Dépense'}</h3>
              {expense.category && (
                <Badge variant="outline">
                  {EXPENSE_CATEGORIES.find((c) => c.value === expense.category)?.label || expense.category}
                </Badge>
              )}
            </div>
            {expense.vendor && (
              <p className="text-sm text-muted-foreground">{expense.vendor}</p>
            )}
            <div className="flex items-center gap-3 mt-1">
                <p className="text-xs text-muted-foreground">
                {formatDate(expense.date || expense.created_at)}
                </p>
                {/* Lien cliquable pour voir l'image du ticket */}
                {expense.image_url && (
                    <a href={expense.image_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary flex items-center gap-1 hover:underline">
                        <FileImage className="w-3 h-3" /> Voir le reçu
                    </a>
                )}
            </div>
            
            {/* Event link */}
            {eventName ? (
              <div className="mt-2 flex items-center gap-1.5">
                <Link2 className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs text-primary font-medium">{eventName}</span>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="mt-1 h-7 text-xs text-muted-foreground"
                onClick={() => onLinkEvent(expense)}
                data-testid={`link-event-${expense.id}`}
              >
                <Link2 className="w-3 h-3 mr-1" /> Lier à un événement
              </Button>
            )}
          </div>

          <div className="text-right">
            <p className="text-lg font-bold">
              {expense.amount ? formatCurrency(expense.amount) : '-'}
            </p>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive mt-2"
              onClick={() => onDelete(expense.id)}
              data-testid={`delete-expense-${expense.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// --- DIALOGUE : UPLOAD + SAISIE MANUELLE ---
const ReceiptUploadDialog = ({ open, onOpenChange, onSave, events }) => {
  const { token } = useAuthStore();
  const fileInputRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    date: '',
    category: 'other',
    vendor: '',
    event_id: 'none',
    image_url: '',
  });

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await fetch(`${API_URL}/expenses/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formDataUpload,
      });

      const data = await res.json();

      if (data.success) {
        setFormData((prev) => ({ ...prev, image_url: data.image_url }));
        toast.success('Image enregistrée. Saisissez les informations.');
      } else {
        toast.error("Erreur lors de l'envoi de l'image");
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    onSave({
      ...formData,
      amount: parseFloat(formData.amount) || null,
      event_id: formData.event_id === 'none' ? null : formData.event_id,
    });
    setFormData({
      description: '', amount: '', date: '', category: 'other', vendor: '', event_id: 'none', image_url: ''
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Ajouter une dépense
          </DialogTitle>
          <DialogDescription>
            Ajoutez la photo de votre reçu, puis complétez les informations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Zone d'image (Upload ou Aperçu) */}
          {!formData.image_url ? (
            <div
              className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors bg-secondary/20"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileSelect}
              />
              {isProcessing ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-sm">Envoi sécurisé en cours...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <Camera className="w-7 h-7 text-primary" />
                  </div>
                  <p className="font-medium text-sm">Prendre en photo / Importer le reçu</p>
                </div>
              )}
            </div>
          ) : (
            <div className="relative border rounded-xl overflow-hidden bg-slate-50 flex justify-center p-2">
                <img src={formData.image_url} alt="Aperçu du reçu" className="max-h-48 object-contain rounded-lg shadow-sm" />
                <Button 
                    variant="secondary" 
                    size="sm" 
                    className="absolute top-2 right-2 opacity-80 hover:opacity-100"
                    onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                >
                    Changer l'image
                </Button>
            </div>
          )}

          {/* Formulaire Manuel */}
          <div className="space-y-4 pt-2 border-t">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Montant TTC (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                <Label>Fournisseur</Label>
                <Input
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    placeholder="Nom du magasin"
                />
                </div>
                <div className="space-y-2">
                <Label>Date (JJ/MM/AAAA)</Label>
                <Input
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    placeholder="DD/MM/YYYY"
                />
                </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Motif de la dépense"
              />
            </div>

            <div className="space-y-2">
              <Label>Lier à un événement</Label>
              <Select
                value={formData.event_id}
                onValueChange={(v) => setFormData({ ...formData, event_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Aucun événement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun événement</SelectItem>
                  {events?.map((evt) => (
                    <SelectItem key={evt.id} value={evt.id}>{evt.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={!formData.amount}>
              Enregistrer la dépense
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --- COMPOSANT PRINCIPAL ---
const ExpensesPage = () => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [linkEventExpense, setLinkEventExpense] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState('none');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data: expenses = [], isLoading } = useQuery({
    queryKey: ['expenses', startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const res = await fetch(`${API_URL}/expenses?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch expenses');
      return res.json();
    },
  });

  const { data: events = [] } = useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch events');
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/expenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to create expense');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Dépense enregistrée');
      setScannerOpen(false);
    },
    onError: () => toast.error('Erreur lors de l\'enregistrement'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/expenses/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete expense');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Dépense supprimée');
    },
    onError: () => toast.error('Erreur lors de la suppression'),
  });

  const linkEventMutation = useMutation({
    mutationFn: async ({ expenseId, eventId }) => {
      const res = await fetch(`${API_URL}/expenses/${expenseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ event_id: eventId }),
      });
      if (!res.ok) throw new Error('Failed to link event');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Dépense liée à l\'événement');
      setLinkEventExpense(null);
    },
    onError: () => toast.error('Erreur lors de la liaison'),
  });

  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">Dépenses</h1>
          <p className="text-muted-foreground">Ajoutez et gérez vos justificatifs</p>
        </div>
        <Button onClick={() => setScannerOpen(true)}>
          <UploadCloud className="w-4 h-4 mr-2" /> Nouvelle dépense
        </Button>
      </div>

      {/* --- BARRE DE FILTRAGE PAR DATE (Style Unifié) --- */}
      <div className="flex flex-col sm:flex-row gap-4 items-center bg-card p-2 rounded-xl border shadow-sm w-fit">
        <div className="flex items-center pl-2 text-muted-foreground">
          <Calendar className="w-4 h-4 mr-2" />
          <span className="text-sm font-medium">Période :</span>
        </div>
        <div className="flex items-center gap-2">
          <Input 
            type="date" 
            value={startDate} 
            onChange={(e) => setStartDate(e.target.value)}
            className="h-9 border-none bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors w-auto text-sm [color-scheme:light_dark]"
          />
          <span className="text-muted-foreground text-sm">au</span>
          <Input 
            type="date" 
            value={endDate} 
            onChange={(e) => setEndDate(e.target.value)}
            className="h-9 border-none bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors w-auto text-sm [color-scheme:light_dark]"
          />
          
          {(startDate || endDate) && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setStartDate(''); setEndDate(''); }}
              className="h-9 px-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
              title="Effacer les dates"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground mb-1">Total des dépenses sur la période</p>
          <p className="text-3xl font-heading font-bold">{formatCurrency(totalExpenses)}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {expenses.length} dépense(s) affichée(s)
          </p>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-8 w-20" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : expenses.length > 0 ? (
        <div className="space-y-3">
          {expenses.map((expense) => (
            <ExpenseCard
              key={expense.id}
              expense={expense}
              events={events}
              onDelete={(id) => {
                if (window.confirm('Supprimer cette dépense ?')) {
                  deleteMutation.mutate(id);
                }
              }}
              onLinkEvent={(exp) => {
                setSelectedEventId(exp.event_id || 'none');
                setLinkEventExpense(exp);
              }}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Receipt className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading font-semibold mb-2">Aucune dépense</h3>
            <p className="text-muted-foreground mb-4">
              {startDate || endDate 
                ? "Aucune dépense trouvée pour cette période." 
                : "Ajoutez votre premier justificatif"}
            </p>
            {!startDate && !endDate && (
              <Button onClick={() => setScannerOpen(true)}>
                <UploadCloud className="w-4 h-4 mr-2" /> Ajouter une dépense
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialogues */}
      <ReceiptUploadDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onSave={(data) => createMutation.mutate(data)}
        events={events}
      />

      <Dialog open={!!linkEventExpense} onOpenChange={(open) => !open && setLinkEventExpense(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Lier à un événement</DialogTitle>
            <DialogDescription>Sélectionnez l'événement à associer à cette dépense</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir un événement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun événement</SelectItem>
                {events.map((evt) => (
                  <SelectItem key={evt.id} value={evt.id}>{evt.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setLinkEventExpense(null)}>Annuler</Button>
              <Button
                onClick={() => {
                  linkEventMutation.mutate({
                    expenseId: linkEventExpense.id,
                    eventId: selectedEventId === 'none' ? null : selectedEventId,
                  });
                }}
              >
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;